import {
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Col,
  Modal,
  Row,
  Table,
} from "reactstrap"
import { MdDelete, MdModeEdit } from "react-icons/md"
import { useEffect, useState } from "react"
import { ItemsPerPage, SelectFilter } from "components/Common/filters"
import { useSelector, useDispatch } from "react-redux"
import {
  allEmailTemplates,
  EmailTemplatesFetch,
  EmailTemplateDelete,
  EmailTemplateCreate,
} from "redux/slice/emailTemplate/emailTemplateSlice"
import PaginationComponent from "components/Common/pagination"
import Loader from "components/Common/loader"
import Notify from "components/Common/notify"
import { SHUK_ROLE_VIWEWER } from "redux/enums"
import { userData } from "redux/slice/user/userSlice"
import { DEFAULT_EMAIL_TEMPLATES } from "common/data/defaultEmailTemplates"
import { collectUsedVariables } from "common/data/emailTemplateVariables"

// Same options as the email template form.
const STATUS_FILTER_OPTIONS = [
  { id: "ACTIVE", name: "Active" },
  { id: "INACTIVE", name: "Inactive" },
]

const COUNTRY_FILTER_OPTIONS = [
  { id: "PK", name: "Pakistan" },
  { id: "IN", name: "India" },
  { id: "BD", name: "Bangladesh" },
  { id: "LK", name: "Sri Lanka" },
]

export default function EmailTemplateTable(props) {
  const dispatch = useDispatch()
  const { userRole } = useSelector(userData)
  const { emailTemplatesData, loading, totalPages } =
    useSelector(allEmailTemplates)

  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState("")
  const [countryFilter, setCountryFilter] = useState("")
  const [deleteIdState, setDeleteIdState] = useState(null)
  const [deleteModalState, setDeleteModalState] = useState(false)
  const [seeding, setSeeding] = useState(false)

  const isViewer = userRole === SHUK_ROLE_VIWEWER

  // Create all the default email templates (used when none exist / were deleted).
  const handleSeedDefaults = async () => {
    setSeeding(true)
    try {
      for (const t of DEFAULT_EMAIL_TEMPLATES) {
        const payload = {
          name: t.name,
          key: t.key,
          subject: t.subject,
          body: t.body,
          description: t.description,
          category: t.category,
          status: t.status,
          countries: t.countries,
          variables: collectUsedVariables(t.body, t.subject),
        }
        // eslint-disable-next-line no-await-in-loop
        await dispatch(EmailTemplateCreate(payload))
      }
      Notify("success", "Default email templates created.")
      dispatch(
        EmailTemplatesFetch({ pageNumber: 1, pageSize: itemsPerPage }),
      )
      setCurrentPage(1)
    } catch (e) {
      console.log("error seeding default templates", e)
      Notify("error", "Failed to create default templates.")
    } finally {
      setSeeding(false)
    }
  }

  useEffect(() => {
    const data = { pageNumber: currentPage, pageSize: itemsPerPage }
    if (statusFilter) data.status = statusFilter
    if (countryFilter) data.country = countryFilter
    dispatch(EmailTemplatesFetch(data))
  }, [dispatch, itemsPerPage, currentPage, statusFilter, countryFilter])

  const paginate = pageNumber => {
    setCurrentPage(pageNumber)
  }

  const handleItemsPerPage = perPage => {
    if (Number(perPage) !== itemsPerPage) {
      setCurrentPage(1)
    }
    setItemsPerPage(Number(perPage))
  }

  const handleStatusFilter = value => {
    setCurrentPage(1)
    setStatusFilter(value)
  }

  const handleCountryFilter = value => {
    setCurrentPage(1)
    setCountryFilter(value)
  }

  const handleUpdate = received => {
    props.forUpdate(received)
  }

  const handleDelete = () => {
    dispatch(EmailTemplateDelete(deleteIdState?.id))
    setDeleteModalState(false)
  }

  return (
    <div className="staff-table-container">
      <Row>
        <Col lg={12}>
          <Card className="app-table-card">
            <CardTitle className="fs-2 dot-after ms-3 mt-2">
              Email Templates
            </CardTitle>
            <CardBody>
              <div
                className="d-flex justify-content-end flex-row align-items-center flex-wrap"
                style={{ width: "100%" }}
              >
                <div className="me-3">
                  <SelectFilter
                    label="Status"
                    placeholder="All"
                    options={STATUS_FILTER_OPTIONS}
                    value={statusFilter}
                    onChange={e => handleStatusFilter(e.target.value)}
                  />
                </div>
                <div className="me-3">
                  <SelectFilter
                    label="Country"
                    placeholder="All"
                    options={COUNTRY_FILTER_OPTIONS}
                    value={countryFilter}
                    onChange={e => handleCountryFilter(e.target.value)}
                  />
                </div>
                <div>
                  <ItemsPerPage
                    value={itemsPerPage}
                    handleItemsPerPage={handleItemsPerPage}
                  />
                </div>
              </div>

              <div className="app-table-body">
                {loading ? (
                  <Loader />
                ) : (
                  <Table className="table mb-0 table-bordered">
                    <thead>
                      <tr>
                        <th className="text-nowrap">Name</th>
                        <th className="text-nowrap">Key</th>
                        <th className="text-nowrap">Subject</th>
                        <th className="text-nowrap">Category</th>
                        <th className="text-nowrap">Status</th>
                        {!isViewer && <th className="text-nowrap">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {emailTemplatesData && emailTemplatesData.length > 0 ? (
                        emailTemplatesData.map(template => (
                          <tr key={template?.id}>
                            <td
                              className="text-nowrap text-truncate"
                              style={{ maxWidth: "180px" }}
                            >
                              {template?.name}
                            </td>
                            <td
                              className="text-nowrap text-truncate"
                              style={{ maxWidth: "160px" }}
                            >
                              {template?.key}
                            </td>
                            <td
                              className="text-nowrap text-truncate"
                              style={{ maxWidth: "220px" }}
                            >
                              {template?.subject}
                            </td>
                            <td className="text-nowrap">
                              {template?.category}
                            </td>
                            <td className="text-nowrap">{template?.status}</td>
                            {!isViewer && (
                              <td className="d-flex">
                                <MdModeEdit
                                  style={{ cursor: "pointer" }}
                                  className="me-2 text-primary"
                                  onClick={() => handleUpdate(template)}
                                  size={18}
                                />
                                <MdDelete
                                  style={{ cursor: "pointer" }}
                                  className="text-danger"
                                  onClick={() => {
                                    setDeleteIdState(template)
                                    setDeleteModalState(true)
                                  }}
                                  size={18}
                                />
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center py-4">
                            <p className="mb-2">No email templates found.</p>
                            {!isViewer && (
                              <button
                                className="btn btn-primary"
                                onClick={handleSeedDefaults}
                                disabled={seeding}
                              >
                                {seeding
                                  ? "Creating…"
                                  : "Create Default Templates"}
                              </button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </CardBody>
            <CardFooter>
              <div className="d-flex justify-content-center justify-content-md-end justify-content-lg-end align-items-center flex-wrap">
                <div style={{ marginLeft: "4px" }}>
                  <PaginationComponent
                    totalPages={totalPages}
                    currentPage={currentPage}
                    paginate={paginate}
                  />
                </div>
              </div>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Modal isOpen={deleteModalState} centered>
        <Card className="bg-white">
          <CardBody className="mt-2">
            <h5 style={{ fontWeight: "normal" }}>
              Are You Sure. You want to Delete ?
            </h5>
          </CardBody>
          <CardFooter>
            <div className="mt-4 float-end">
              <button
                className="btn btn-danger me-2"
                onClick={() => handleDelete()}
              >
                Yes
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setDeleteModalState(false)}
              >
                No
              </button>
            </div>
          </CardFooter>
        </Card>
      </Modal>
    </div>
  )
}
